import { useEffect, useState, useMemo, ComponentType, ReactNode } from 'react';
import { evaluate } from '@mdx-js/mdx';
import * as runtime from 'react/jsx-runtime';
import { useTheme } from '@/contexts/ThemeContext';

type MDXComponentProps = {
  components?: Record<string, ComponentType<any>>;
  [key: string]: unknown;
};

type MDXComponent = ComponentType<MDXComponentProps>;

export function MarkdownRenderer({
  source,
  components = {},
  noPadding = false,
}: {
  source: string;
  components?: Record<string, React.ComponentType<any>>;
  noPadding?: boolean;
}) {
  const [Content, setContent] = useState<MDXComponent | null>(null);
  const { isDark } = useTheme();
  console.log('[MDX Renderer] isDark:', isDark)
  // console.log('[MDX Renderer] Content:', Content);
  useEffect(() => {
    const compile = async () => {
      try {
        const compiled = await evaluate(source, {
          ...runtime,
          jsx: runtime.jsx,
          jsxs: runtime.jsxs,
          jsxDEV: runtime.jsxDEV,
          useDynamicImport: false,
          baseUrl: import.meta.url,
        });

        setContent(() => compiled.default);
      } catch (e) {
        console.error('MDX runtime error:', e);
        setContent(() => () => <p>Error rendering MDX.</p>);
      }
    };

    compile();
  }, [source, components]);

  // Base styles that won't be overridden by globals
  const baseStyles = useMemo(() => ({
    maxWidth: '56rem',
    marginLeft: 'auto',
    marginRight: 'auto',
    padding: noPadding ? '0' : '0px 1rem 1.5rem',
    transition: 'color 0.2s, background-color 0.2s',
    // Light theme defaults
    color: isDark ? '#e5e7eb' : '#1f2937',
    backgroundColor: 'transparent',
  }), [isDark, noPadding]);

  const componentStyles = useMemo(() => ({
    h1: {
      fontSize: '1.875rem',
      fontWeight: 700,
      marginBottom: '1.5rem',
      marginTop: '2.5rem',
      paddingBottom: '0.5rem',
      borderBottom: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
      color: isDark ? '#e5e7eb' : '#1f2937',
    },
    h2: {
      fontSize: '1.5rem',
      fontWeight: 600,
      marginBottom: '1rem',
      marginTop: '2rem',
      paddingBottom: '0.5rem',
      borderBottom: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
      color: isDark ? '#e5e7eb' : '#1f2937',
    },
    h3: {
      fontSize: '1.25rem',
      fontWeight: 500,
      marginBottom: '0.75rem',
      marginTop: '1.5rem',
      color: isDark ? '#e5e7eb' : '#1f2937',
    },
    h4: {
      fontSize: '1.125rem',
      fontWeight: 500,
      marginBottom: '0.5rem',
      marginTop: '1.25rem',
      color: isDark ? '#e5e7eb' : '#1f2937',
    },
    h5: {
      fontSize: '1rem',
      fontWeight: 600,
      marginBottom: '0.5rem',
      marginTop: '1rem',
      color: isDark ? '#e5e7eb' : '#1f2937',
    },
    h6: {
      fontSize: '0.875rem',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      marginBottom: '0.5rem',
      marginTop: '1rem',
      color: isDark ? '#e5e7eb' : '#1f2937',
    },
    p: {
      marginBottom: '1rem',
      lineHeight: 1.75,
      color: isDark ? '#d1d5db' : '#374151',
    },
    a: {
      color: isDark ? '#60a5fa' : '#3b82f6',
      textDecoration: 'none',
      transition: 'color 0.2s, text-decoration-color 0.2s',
      textUnderlineOffset: '2px',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    ul: {
      listStyleType: 'disc',
      paddingLeft: '1.5rem',
      marginTop: '1rem',
      marginBottom: '1rem',
    },
    ol: {
      listStyleType: 'decimal',
      paddingLeft: '1.5rem',
      marginTop: '1rem',
      marginBottom: '1rem',
    },
    li: {
      marginTop: '0.25rem',
      marginBottom: '0.25rem',
    },
    blockquote: {
      borderLeft: `4px solid ${isDark ? '#374151' : '#e5e7eb'}`,
      paddingLeft: '1rem',
      fontStyle: 'italic',
      color: isDark ? '#d1d5db' : '#374151',
      margin: '1rem 0',
    },
    hr: {
      borderColor: isDark ? '#374151' : '#e5e7eb',
      margin: '1.5rem 0',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      marginTop: '1rem',
      marginBottom: '1rem',
    },
    thead: {
      backgroundColor: isDark ? 'rgba(55,65,81,0.3)' : 'rgba(243,244,246,0.6)',
    },
    tbody: {},
    tr: {
      borderBottom: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
    },
    th: {
      textAlign: 'left',
      fontWeight: 600,
      padding: '0.5rem',
    },
    td: {
      padding: '0.5rem',
      verticalAlign: 'top',
    },
    img: {
      maxWidth: '100%',
      borderRadius: '0.25rem',
    },
    strong: {
      fontWeight: 600,
    },
    em: {
      fontStyle: 'italic',
    },
    del: {
      opacity: 0.7,
    },
    code: {
      backgroundColor: isDark ? 'rgba(55,65,81,0.5)' : 'rgba(243,244,246,0.8)',
      padding: '0.125rem 0.375rem',
      borderRadius: '0.25rem',
      fontSize: '0.875rem',
    },
    pre: {
      backgroundColor: isDark ? 'rgba(55,65,81,0.5)' : 'rgba(243,244,246,0.8)',
      padding: '1rem',
      borderRadius: '0.5rem',
      overflowX: 'auto',
      margin: '1rem 0',
    },
  }), [isDark]);

  const renderComponent = (Component: ComponentType<any>, style: any, tag: string) => 
    ({ children, ...props }: { children: ReactNode; [key: string]: unknown }) => {
      const hoverProps = tag === 'a' ? {
        onMouseOver: (e: React.MouseEvent<HTMLAnchorElement>) => {
          e.currentTarget.style.textDecoration = 'underline';
        },
        onMouseOut: (e: React.MouseEvent<HTMLAnchorElement>) => {
          e.currentTarget.style.textDecoration = 'none';
        }
      } : {};
      
      return (
        <Component {...props} style={style} {...hoverProps}>
          {children}
        </Component>
      );
    };

  return Content ? (
    <div style={baseStyles}>
      <Content components={{
        ...components,
        h1: renderComponent('h1' as any, componentStyles.h1, 'h1'),
        h2: renderComponent('h2' as any, componentStyles.h2, 'h2'),
        h3: renderComponent('h3' as any, componentStyles.h3, 'h3'),
        h4: renderComponent('h4' as any, componentStyles.h4, 'h4'),
        h5: renderComponent('h5' as any, componentStyles.h5, 'h5'),
        h6: renderComponent('h6' as any, componentStyles.h6, 'h6'),
        p: renderComponent('p' as any, componentStyles.p, 'p'),
        a: renderComponent('a' as any, componentStyles.a, 'a'),
        ul: renderComponent('ul' as any, componentStyles.ul, 'ul'),
        ol: renderComponent('ol' as any, componentStyles.ol, 'ol'),
        li: renderComponent('li' as any, componentStyles.li, 'li'),
        blockquote: renderComponent('blockquote' as any, componentStyles.blockquote, 'blockquote'),
        hr: renderComponent('hr' as any, componentStyles.hr, 'hr'),
        table: renderComponent('table' as any, componentStyles.table, 'table'),
        thead: renderComponent('thead' as any, componentStyles.thead, 'thead'),
        tbody: renderComponent('tbody' as any, componentStyles.tbody, 'tbody'),
        tr: renderComponent('tr' as any, componentStyles.tr, 'tr'),
        th: renderComponent('th' as any, componentStyles.th, 'th'),
        td: renderComponent('td' as any, componentStyles.td, 'td'),
        img: renderComponent('img' as any, componentStyles.img, 'img'),
        strong: renderComponent('strong' as any, componentStyles.strong, 'strong'),
        em: renderComponent('em' as any, componentStyles.em, 'em'),
        del: renderComponent('del' as any, componentStyles.del, 'del'),
        code: renderComponent('code' as any, componentStyles.code, 'code'),
        pre: renderComponent('pre' as any, componentStyles.pre, 'pre'),
      }} />
    </div>
  ) : null;
}
